import { useState } from 'react';
import {
  BookOpen,
  Shield,
  Users,
  AlertTriangle,
  Monitor,
  LayoutDashboard,
  FileBarChart2,
  Award,
  ArrowLeft,
  ChevronRight,
  Clock,
  Phone,
} from 'lucide-react';

type ContentBlock =
  | { type: 'text'; text: string }
  | { type: 'image'; src: string; alt: string; caption?: string; size?: 'sm' | 'md' | 'full' };

interface DocArticle {
  id: string;
  category: string;
  tag: string;
  title: string;
  summary: string;
  readTime: string;
  icon: React.ElementType;
  content: ContentBlock[];
}

const DOCS: DocArticle[] = [
  // how to register to be added with images 
  {
    id: 'getting-started',
    category: 'Getting Started',
    tag: 'Setup',
    title: 'Setting Up Your Teacher Dashboard',
    summary:
      'How to register for the first time and have your class roster active and monitoring live.',
    readTime: '3 min read',
    icon: BookOpen,
    content: [
      { type: 'text', text: 'After your school admin sends your invite link, complete registration and log in. You will land on the Teacher Dashboard, which shows your at-a-glance safety summary, recent alerts, and weekly trends for your classes.' },
      {
        type: 'image',
        src: '/dashboard-sidebar.png',
        alt: 'ShieldEd Haven sidebar with arrow pointing to Dashboard',
        size: 'sm',
      },
      { type: 'text', text: 'The left sidebar is your primary navigation. Sections include Dashboard, Live Activity, Alerts, My Classes, Students, Reports, Notifications, and Gamification. The active section is highlighted in blue.' },
      //include this image when data available
      { type: 'text', text: 'Your first task is to confirm your class roster under My Classes. If students have already been enrolled by your school admin, they will appear here. You can add or move students between classes if your admin has granted you that permission.' },
      { type: 'text', text: 'Enable push notifications when prompted — this ensures you are alerted within 60 seconds whenever a student triggers a medium or higher severity flag. You can manage notification preferences in your Profile settings.' },
      { type: 'text', text: 'Once your roster is confirmed, switch to Live Activity tab to see browser events flowing in as students work. Within 24 hours of your first students going online, your Dashboard metrics and charts will begin populating.' },
    ],
  },
  {
    id: 'push-notifications',
    category: 'Getting Started',
    tag: 'Setup',
    title: 'How to Enable Push Notifications',
    summary:
      'Enable push notifications to receive real-time alerts on your desktop or mobile device whenever a student triggers a medium or higher severity flag.',
    readTime: '1 min read',
    icon: Phone,
    content: [
      { type: 'text', text: 'Log in and look at the left sidebar. At the bottom you will find the Settings option.' },
      {
        type: 'image',
        src: '/sidebar-settings.png',
        alt: 'ShieldEd Haven sidebar with arrow pointing to Settings',
        size: 'sm',
      },
      { type: 'text', text: 'Step 1 — Click Settings in the left sidebar. Inside Settings, navigate to Notification Preferences.' },
      {
        type: 'image',
        src: '/notification-preferences.png',
        alt: 'Notification Preferences panel with Browser notifications toggled on',
        size: 'sm',
      },
      {type: 'text', text: 'Step 2 — Enable Browser notifications and confirm the toggle is blue'}
    ],
  },
  {
    id: 'dashboard',
    category: 'Getting Started',
    tag: 'Overview',
    title: 'Reading Your Dashboard',
    summary:
      'Understand everything on the Teacher Dashboard home page.',
    readTime: '4 min read',
    icon: LayoutDashboard,
    content: [
      {
        type: 'image',
        src: '/header.png',
        alt: 'ShieldEd Haven header bar',
      },
      { type: 'text', text: 'The header bar shows the current time and date, a notification bell, a student search field, dark or light toggle mode, and your profile. ' },
      
      {
        type: 'image',
        src: '/metric-row-dashboard.png',
        alt: 'ShieldEd Haven metric row',
      },
      { type: 'text', text: "The top metric row displays four live numbers: Active Alerts, Students Monitored, Incidents Resolved, and Average Response Time  (total browser events logged today)." },
      
      {
        type: 'image',
        src: '/active-alerts.png',
        alt: 'ShieldEd Haven active alerts panel',
      },
      { type: 'text', text: 'The Active  Alerts lists the five most recent unresolved alerts with severity badges. Clicking any row takes you directly to the full alert detail with ML confidence scores and the raw flagged content.' },
      
      {
        type: 'image',
        src: '/quick-actions.png',
        alt: 'ShieldEd Haven quick actions panel',
      },
      { type: 'text', text: 'The quick actions panel have quick links to review alerts, view students, gamification, and notifications.' },

      //need data on weekly heatmap to add image
      { type: 'text', text: 'Below the metrics is a weekly heatmap showing flagged-event density by day and hour. Darker cells indicate higher alert volume — useful for spotting when students are most at risk.' },
      

      {
        type: 'image',
        src: '/requiring-attention.png',
        alt: 'ShieldEd Haven requiring attention panel',
      },
      { type: 'text', text: 'The bottom row shows students requiring attention sorted by risk score. ' },
    ],
  },
  {
    id: 'live-activity',
    category: 'Monitoring',
    tag: 'Live',
    title: 'Using the Live Activity Feed',
    summary:
      'The Live Activity feed streams browser events from your students in real time. Learn how to read events, filter by student, and act on what you see.',
    readTime: '3 min read',
    icon: Monitor,
    content: [
      { type: 'text', text: 'In the left sidebar, click Live Activity. You will see a green dot next to it when students are currently online.' },
      {
        type: 'image',
        src: '/live-activity-sidebar.png',
        alt: 'Sidebar with Live Activity highlighted and green dot visible',
        size: 'sm',
      },
      { type: 'text', text: 'Step 1 — Click Live Activity in the left sidebar. The green dot means students are online now.'},
      {
        type: 'image',
        src: '/live-activity-refreshes.png',
        alt: 'Live Activity feed showing events list with student names and Safe badges',
      },
      { type: 'text', text: 'Step 2 — Each row shows the student name, the website they visited, how long ago, and whether it was Safe or Flagged.'},
      { type: 'text', text: 'Use the All / Flagged / Safe toggle buttons at the top right of the feed to filter events.'},
      { type: 'text', text: 'The feed auto-refreshes every 15 seconds. You will see a "Last updated Xs ago" timestamp at the top. You can also click the Refresh button manually at any time.' },
    ],
  },
  {
    id: 'reading-alerts',
    category: 'Incidents',
    tag: 'Workflow',
    title: 'How to Read an Alert',
    summary:
      'Understand every field in the Alert Details panel.',
    readTime: '3 min read',
    icon: AlertTriangle,
    content: [
      {
        type: 'image',
        src: '/alerts-sidebar.png',
        alt: 'Sidebar with Alerts highlighted in blue',
        size: 'sm',
      },
      { type: 'text', text: 'Step 1 — Click Alerts in the left sidebar.'},
      { type: 'text', text: 'At the top you will see the detection type (e.g. CYBERBULLYING Detected), a short excerpt of the flagged content, and three badges: severity (HIGH / MEDIUM / LOW), status (NEW / OPEN), and category.' },
      {
        type: 'image',
        src: '/alert-detail-confidence.png',
        alt: 'Alert Details modal showing CYBERBULLYING Detected with HIGH badge',
      },
      {type: 'text', text: 'Step 2 — The top of the panel shows detection type, severity, and status badges.'},
      { type: 'text', text: 'Below the header is the Student Information section. This shows the student\'s name (blurred for privacy in this guide), email, school, and the exact date and time the content was detected.' },
    
      {type: 'text', text: 'Step 3 — The AI Confidence bar. Red and near 100% means high certainty. Always review the Flagged Content box below it.'},
      {type: 'text', text: 'At the very bottom of the panel is the Flagged Content box — this shows the exact text or URL the AI flagged. Read this carefully before taking any action.' },
    ],
  },
  {
    id: 'acknowledge-alert',
    category: 'Incidents',
    tag: 'Workflow',
    title: 'How to Acknowledge an Alert',
    summary:
      'Acknowledging an alert signals to your admin that you have seen it and are aware of the situation.',
    readTime: '1 min read',
    icon: AlertTriangle,
    content: [
      {
        type: 'image',
        src: '/alerts-sidebar.png',
        alt: 'Sidebar with Alerts highlighted in blue',
        size: 'sm',
      },
      { type: 'text', text: 'In the left sidebar, click Alerts to open the alerts list.' },

      {
        type: 'image',
        src: '/action-acknowledge.png',
        alt: 'Status Actions bar with arrow pointing to Acknowledge button',
        caption: 'Clicking Acknowledge changes the alert status from NEW to ACKNOWLEDGED',
      },
      { type: 'text', text: 'Open the Alert Details panel by clicking any alert in the Alerts list. Scroll to the STATUS ACTIONS section at the bottom of the panel.' },
      { type: 'text', text: 'Your name and timestamp are recorded in the audit log. This does not resolve the alert — it simply confirms you are aware and handling it.' },
    ],
  },
  {
    id: 'investigate-alert',
    category: 'Incidents',
    tag: 'Workflow',
    title: 'How to Investigate an Alert',
    summary:
      'Mark an alert as under investigation while you gather more context before deciding on next steps.',
    readTime: '1 min read',
    icon: AlertTriangle,
    content: [
      {
        type: 'image',
        src: '/alerts-sidebar.png',
        alt: 'Sidebar with Alerts highlighted in blue',
        size: 'sm',
      },
      { type: 'text', text: 'In the left sidebar, click Alerts to open the alerts list.' },
      { type: 'text', text: 'Open the Alert Details panel and scroll to the STATUS ACTIONS section. Click the Investigate button.' },
      {
        type: 'image',
        src: '/action-investigate.png',
        alt: 'Status Actions bar with arrow pointing to Investigate button',
        caption: 'Click Investigate to flag this alert as actively being looked into.',
      },
      { type: 'text', text: 'The alert status updates to INVESTIGATING. Use this time to review the student\'s activity history, speak with the student privately, or gather additional context from the Live Activity feed before resolving or escalating.' },
    ],
  },
  {
    id: 'resolve-alert',
    category: 'Incidents',
    tag: 'Workflow',
    title: 'How to Resolve an Alert',
    summary:
      'Close out an alert once you have reviewed it and determined the appropriate response.',
    readTime: '1 min read',
    icon: AlertTriangle,
    content: [
      {
        type: 'image',
        src: '/alerts-sidebar.png',
        alt: 'Sidebar with Alerts highlighted in blue',
        size: 'sm',
      },
      { type: 'text', text: 'In the left sidebar, click Alerts to open the alerts list.' },
      { type: 'text', text: 'Open the Alert Details panel and scroll to the STATUS ACTIONS section. Click the Resolve button.' },
      {
        type: 'image',
        src: '/action-resolve.png',
        alt: 'Status Actions bar with arrow pointing to Resolve button',
        caption: 'Click Resolve once you have reviewed the alert and taken appropriate action.',
      },
      { type: 'text', text: 'Resolved alerts are moved to the Resolved tab and archived for 90 days. All state changes are written to the Audit Log with your name and a timestamp. If parent notification is enabled, a summary email may be sent to the parent or guardian.' },
    ],
  },
  {
    id: 'escalate-alert',
    category: 'Incidents',
    tag: 'Workflow',
    title: 'How to Escalate an Alert to Admin',
    summary:
      'Send a high-severity alert immediately to your school administrator when the situation requires senior intervention.',
    readTime: '1 min read',
    icon: AlertTriangle,
    content: [
      {
        type: 'image',
        src: '/alerts-sidebar.png',
        alt: 'Sidebar with Alerts highlighted in blue',
        size: 'sm',
      },
      { type: 'text', text: 'In the left sidebar, click Alerts to open the alerts list.' },
      { type: 'text', text: 'Open the Alert Details panel and scroll to the STATUS ACTIONS section. Click Escalate to Admin.' },
      {
        type: 'image',
        src: '/action-escalate.png',
        alt: 'Status Actions bar with arrow pointing to Escalate to Admin button',
        caption: 'Click Escalate to Admin to immediately notify your school administrator.',
      },
      { type: 'text', text: 'Your school admin receives an immediate push notification and email with a link directly to this alert. Use escalation for Critical or High severity alerts where you believe senior oversight or external intervention (e.g. contacting parents, emergency services) may be needed.' },
    ],
  },
  {
    id: 'contact-parent',
    category: 'Incidents',
    tag: 'Workflow',
    title: 'How to Contact a Parent',
    summary:
      'Reach out directly to a student\'s registered parent or guardian from within the Alert Details panel.',
    readTime: '1 min read',
    icon: AlertTriangle,
    content: [
      {
        type: 'image',
        src: '/alerts-sidebar.png',
        alt: 'Sidebar with Alerts highlighted in blue',
        size: 'sm',
      },
      { type: 'text', text: 'In the left sidebar, click Alerts to open the alerts list.' },
      { type: 'text', text: 'Open the Alert Details panel and scroll to the QUICK CONTACT section below the STATUS ACTIONS.' },
      {
        type: 'image',
        src: '/action-contact-parent.png',
        alt: 'Quick Contact section with arrow pointing to Contact Parent button',
        caption: 'Click Contact Parent to open a pre-filled email to the student\'s registered parent or guardian.',
      },
      { type: 'text', text: 'A pre-filled email draft will open with the parent\'s registered email address, the alert summary, and a standard message template. Review and edit the message before sending. The sent email is logged against the alert in the audit trail.' },
    ],
  },
  {
    id: 'assign-counselor',
    category: 'Incidents',
    tag: 'Workflow',
    title: 'How to Assign to a Counselor',
    summary:
      'Route a sensitive alert directly to your school counselor for professional follow-up.',
    readTime: '1 min read',
    icon: AlertTriangle,
    content: [
      {
        type: 'image',
        src: '/alerts-sidebar.png',
        alt: 'Sidebar with Alerts highlighted in blue',
        size: 'sm',
      },
      { type: 'text', text: 'In the left sidebar, click Alerts to open the alerts list.' },
      { type: 'text', text: 'Open the Alert Details panel and scroll to the QUICK CONTACT section. Click Assign to Counselor.' },
      {
        type: 'image',
        src: '/action-counselor.png',
        alt: 'Quick Contact section with arrow pointing to Assign to Counselor button',
        caption: 'Click Assign to Counselor to route this alert for professional pastoral follow-up.',
      },
      { type: 'text', text: 'The counselor receives a notification with the alert details and a link to the full panel. The alert is tagged as "Referred to Counselor" in the audit log. This is recommended for self-harm, mental health, and bullying alerts where professional intervention is appropriate.' },
    ],
  },
  {
    id: 'gamification',
    category: 'Classroom',
    tag: 'Engagement',
    title: 'Gamification — Points, Badges & Leaderboards',
    summary:
      'How the gamification system rewards positive student behaviour and how you can use it to reinforce safety habits in your class.',
    readTime: '3 min read',
    icon: Award,
    content: [
      {
        type: 'image',
        src: '/gamification-sidebar.png',
        alt: 'Sidebar with Gamification highlighted in blue',
        size: 'sm',
      },
      { type: 'text', text: 'Step 1 — Click Gamification in the left sidebar.' },
      { type: 'text', text: 'At the top of the page you will see the Weekly Safety Champion — the student with the highest safety score this week. The champion banner shows their name and score out of 100.' },
      {
        type: 'image',
        src: '/gamification-leaderboard.png',
        alt: 'Safety Leaderboard showing Weekly Safety Champion with score 74/100 and top students ranked by points',
        size: 'md', 
      },
      { type: 'text', text: 'Step 2 — The champion banner highlights the top student. Below it, the class average score, students monitored, and low-risk count are shown.' },
      { type: 'text', text: 'The Top Students leaderboard ranks all students by total points. Each row shows their rank, name, a progress bar, and total points. Students earn points by completing safety games, self-correcting flagged content, and receiving teacher rewards.' },
      { type: 'text', text: 'UPCOMING FEATURE: As a teacher you will be able to award bonus points directly from this page. For now, points are earned automatically based on student behaviour in the platform.' },
      { type: 'text', text: 'Research shows that combining positive reinforcement (points, badges) with safety education is more effective than monitoring alone. Celebrate class milestones and encourage students to check their progress regularly.' },
    ],
  },
  { //need data to add images
    id: 'classes',
    category: 'Classroom',
    tag: 'Management',
    title: 'Managing Your Classes',
    summary:
      'How to view class rosters, add or move students, and adjust per-class monitoring settings.',
    readTime: '3 min read',
    icon: Users,
    content: [
      { type: 'text', text: 'My Classes shows each class you are assigned to, with a student count, average safety score, and open alert count. Click a class card to open the full roster.' },
      { type: 'text', text: "Within the roster, each student row shows their current safety score, points earned, and last-seen timestamp. A red indicator next to a student's name means they have an open unresolved alert." },
      { type: 'text', text: 'To add a student to a class, click "Add Student" and enter their email address. They must already have a student account in your school. If they are new, ask your school admin to enrol them first.' },
      { type: 'text', text: 'You can move a student between your classes using the "Move to Class" dropdown on their profile row. This does not affect their historical data — all activity before the move is retained.' },
      { type: 'text', text: 'Class-level settings (such as blocking specific URL categories during class hours) are available if your school admin has enabled them. Contact your admin if you do not see these controls.' },
    ],
  },
  {
    id: 'students',
    category: 'Monitoring',
    tag: 'Profiles',
    title: 'Student Risk Profiles',
    summary:
      "Deep-dive into an individual student's activity history, alert timeline, safety score trend, and gamification progress.",
    readTime: '4 min read',
    icon: Shield,
    content: [
      { type: 'text', text: 'In the left sidebar, click Students to open the full student list.' },
      
      { type: 'image', src: '/students-sidebar.png', alt: 'Sidebar with Students highlighted in blue', size: 'sm' },
      { type: 'text', text: 'The Students page shows all monitored students.' },
      
      { type: 'image', src: '/students-card-detail.png', alt: 'Students list showing filter buttons and a student card with High Risk badge', size: 'full' },
      { type: 'text', text: 'Each student card shows their name, email, risk score as a percentage bar, number of alerts this month, last activity time, online status, and whether their risk trend is increasing or decreasing. A red bar and High Risk badge means immediate attention is recommended.' },
      
      { type: 'image', src: '/students-list.png', alt: 'Student card showing 56 alerts, 2d ago, Offline, Increasing trend, View Profile and Flag for Review buttons', size: 'full' },
      { type: 'text', text: 'Click View Profile to open the full student profile, or click Flag for Review to immediately escalate the student to your admin without opening the profile.' },
      { type: 'text', text: 'Inside the student profile you will see their risk score, total alerts this month, last activity timestamp, and trend indicator. Below that is the Recent Alerts panel — click any alert row to open its full detail.' },
      
      { type: 'image', src: '/students-profile.png', alt: 'Student profile showing risk score 80%, 56 alerts, recent alerts list with CYBERBULLYING Detected entries', size: 'md' },
      { type: 'text', text: 'Click any alert in the Recent Alerts list to open the full Alert Details panel where you can read the flagged content, review the AI confidence score, and take action.' },
    ],
  },
  { // need more data to add images
    id: 'reports',
    category: 'Reports',
    tag: 'Analytics',
    title: 'Reports & Analytics',
    summary:
      'How to use trend charts, filter by date range or class, and export data for parent meetings or administrative reviews.',
    readTime: '4 min read',
    icon: FileBarChart2,
    content: [
      { type: 'text', text: 'The Reports page has three tabs: Overview (aggregate trends for all your classes), By Class (per-class comparison charts), and By Student (individual drill-down tables).' },
      { type: 'text', text: 'The Overview tab shows a 30-day trend of total events, flagged events, and resolution rate. The detection-category breakdown pie chart shows which types of content (cyberbullying, self-harm, etc.) are most prevalent.' },
      { type: 'text', text: 'Use the date picker at the top right to change the reporting window. Supported ranges are 7 days, 30 days, 90 days, and custom. All charts update immediately when you change the range.' },
      { type: 'text', text: 'The By Student table can be sorted by any column — click a column header to sort ascending, click again for descending. This makes it easy to rank students by most flags, lowest safety score, or fewest points earned.' },
      { type: 'text', text: 'To export a report, click the "Export PDF" or "Export CSV" button in the top right. PDF exports include all visible charts and are suitable for parent meetings. CSV exports include raw event-level data for further analysis.' },
    ],
  },
];

const CATEGORIES = ['All', ...Array.from(new Set(DOCS.map(d => d.category)))];

export function TeacherDocumentation({ dm }: { dm: boolean }) {
  const [activeCategory, setActiveCategory] = useState('All');
  const [openId, setOpenId] = useState<string | null>(null);

  const filtered    = activeCategory === 'All' ? DOCS : DOCS.filter(d => d.category === activeCategory);
  const openArticle = DOCS.find(d => d.id === openId);

  // ── Shared tokens ──────────────────────────────────────────────────────────
  const glassCard = dm
    ? 'bg-slate-900/40 backdrop-blur-md border border-white/[0.1] shadow-lg'
    : 'bg-white/70 backdrop-blur-md border border-slate-200/50 shadow-md';

  const heading    = dm ? 'text-slate-50'  : 'text-slate-900';
  const subheading = dm ? 'text-slate-400' : 'text-slate-600';
  const body       = dm ? 'text-slate-300' : 'text-slate-700';
  const muted      = dm ? 'text-slate-500' : 'text-slate-500';
  const divider    = dm ? 'border-white/[0.08]' : 'border-slate-200/50';

  const tagPill = dm
    ? '!bg-slate-800/50 !text-slate-300'
    : '!bg-slate-100 !text-slate-600';

  const cardHover = dm
    ? 'hover:border-white/[0.15] hover:shadow-xl'
    : 'hover:border-slate-300/60 hover:shadow-lg';

  return (
    <div className="max-w-5xl mx-auto px-6 py-12">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="mb-10">
        <div className="space-y-4 mb-8">
          <h1 className={`text-3xl font-bold tracking-tight ${heading}`}>Documentation</h1>
          <p className={`text-base leading-relaxed ${subheading} max-w-2xl`}>
            Guides and reference material for managing your classes and keeping students safe.
          </p>
        </div>

        {/* Category chips — index only */}
        {!openArticle && (
          <div className="flex flex-wrap gap-2.5">
            {CATEGORIES.map(cat => {
              const active = activeCategory === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className="px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap"
                  style={{
                    backgroundColor: active ? (dm ? 'rgb(51 65 85 / 0.8)' : 'rgb(30 41 59)') : 'transparent',
                    color:           active ? 'white' : (dm ? 'rgb(148 163 184)' : 'rgb(71 85 105)'),
                    border:          active ? 'none' : (dm ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(203,213,225,0.5)'),
                  }}
                >
                  {cat}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Content ────────────────────────────────────────────────────────── */}
      <div>
        {openArticle ? (
          /* ── Article reading view ──────────────────────────────────────── */
          <div className="space-y-6">
            <button
              onClick={() => setOpenId(null)}
              className="inline-flex items-center gap-2 text-sm font-medium transition-colors mb-4"
              style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', padding: 0, color: dm ? 'rgb(148 163 184)' : 'rgb(71 85 105)' }}
            >
              <ArrowLeft className="h-4 w-4" />
              Back to all articles
            </button>

            <article className={`rounded-2xl ${glassCard} p-10 space-y-6`}>
              <div className="space-y-4">
                <div className={`flex items-center gap-3 text-sm ${muted}`}>
                  <span className="font-medium">{openArticle.category}</span>
                  <span className={dm ? 'text-slate-600' : 'text-slate-300'}>·</span>
                  <span>{openArticle.tag}</span>
                  <span className={dm ? 'text-slate-600' : 'text-slate-300'}>·</span>
                  <Clock className="h-4 w-4" />
                  <span>{openArticle.readTime}</span>
                </div>
                <h2 className={`text-3xl font-bold leading-tight ${heading}`}>
                  {openArticle.title}
                </h2>
                <p className={`text-base leading-relaxed ${subheading}`}>
                  {openArticle.summary}
                </p>
              </div>

              <hr className={`border-t ${divider}`} />

              <div className="space-y-5">
                {openArticle.content.map((block, i) => (
                  <div key={i}>
                    {block.type === 'text' && (
                      <p className={`text-base leading-7 ${body}`}>{block.text}</p>
                    )}
                    {block.type === 'image' && (
                      <figure className={`my-2 ${
                        block.size === 'sm'   ? 'w-1/4' :
                        block.size === 'md'   ? 'w-1/2' :
                        'w-full'
                      }`}>
                          <img
                            src={block.src}
                            alt={block.alt}
                            className="rounded-xl border w-full object-cover shadow-sm"
                            style={{ borderColor: dm ? 'rgba(255,255,255,0.08)' : 'rgba(203,213,225,0.5)' }}
                          />
                          {block.caption && (
                            <figcaption className={`text-xs mt-2 text-center ${muted}`}>
                              {block.caption}
                            </figcaption>
                          )}
                        </figure>
                    )}
                  </div>
                ))}
              </div>
            </article>
          </div>

        ) : (
          /* ── Article grid ──────────────────────────────────────────────── */
          <div className="grid gap-5">
            {filtered.map(doc => {
              const Icon = doc.icon;
              return (
                <button
                  key={doc.id}
                  onClick={() => setOpenId(doc.id)}
                  className={`w-full text-left p-6 flex items-start gap-5 group rounded-2xl transition-all duration-300 ${glassCard} ${cardHover}`}
                  style={{
                    backgroundColor: dm ? 'rgba(15,23,42,0.4)' : 'rgba(255,255,255,0.7)',
                    borderColor:     dm ? 'rgba(255,255,255,0.1)' : 'rgba(203,213,225,0.5)',
                  }}
                >
                  {/* Icon */}
                  <div className={`mt-1 h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${dm ? 'bg-white/[0.1]' : 'bg-slate-100'}`}>
                    <Icon className={`h-5 w-5 ${muted}`} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0 space-y-2.5">
                    <div className="flex items-center gap-3">
                      <span className={`text-xs px-3 py-1 rounded-full font-medium ${tagPill}`}>
                        {doc.tag}
                      </span>
                      <span className={`text-xs ${muted}`}>{doc.readTime}</span>
                    </div>
                    <h3 className={`text-lg font-semibold leading-snug ${heading} ${dm ? 'group-hover:text-white' : 'group-hover:text-slate-900'} transition-colors`}>
                      {doc.title}
                    </h3>
                    <p className={`text-sm leading-relaxed ${subheading} line-clamp-2`}>
                      {doc.summary}
                    </p>
                  </div>

                  {/* Arrow */}
                  <ChevronRight className={`h-5 w-5 mt-2 shrink-0 opacity-0 group-hover:opacity-70 transition-all duration-300 group-hover:translate-x-1 ${muted}`} />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}